import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useContext } from "react";
import { fromJS } from "immutable";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AppContext } from "./Contexts";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  PlusIcon,
  UserIcon,
  UserPlusIcon,
  ArrowRightStartOnRectangleIcon,
  CurrencyDollarIcon,
  Squares2X2Icon,
  UserGroupIcon,
  BriefcaseIcon,
  ChartPieIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  BuildingOffice2Icon,
  LifebuoyIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { handleOnClickProject } from "./AppBreadcrumb";
import AIContentAction from "./actions/AIContentAction";
import useUpgradePlan from "./team/hooks/useUpgradePlan";
import { PackagePlusIcon } from "lucide-react";
import { logout } from "./api/StorageApi";
import { updateCurrentUserFilters } from "./api/UserApi";
import type { ComponentType } from "react";

/* ── Type → Icon mapping ── */
const WORKSPACE_TYPE_ICONS: Record<
  string,
  ComponentType<{ className?: string; style?: React.CSSProperties }>
> = {
  SALES: CurrencyDollarIcon,
  PROJECTS: Squares2X2Icon,
  PARTNERSHIPS: UserGroupIcon,
  HIRING: UserPlusIcon,
  JOB_SEARCH: BriefcaseIcon,
  INVESTOR: ChartPieIcon,
  FUNDRAISING: BanknotesIcon,
  ORDERS: ShoppingCartIcon,
  REAL_ESTATE: BuildingOffice2Icon,
  SUPPORT: LifebuoyIcon,
  CUSTOM: WrenchScrewdriverIcon,
};

export const DEFAULT_WORKSPACE_COLOR = "#3b82f6";

export const getWorkspaceColor = (item: any): string => {
  const color = item.get?.("color") || item.color;
  if (color && color !== "" && color !== "#") return color;
  return DEFAULT_WORKSPACE_COLOR;
};

const getWorkspaceType = (item: any): string => {
  return item.get?.("type") || item.type || "SALES";
};

/* ── Pipeline Icon (small colored circle with type icon) ── */
export const PipelineIcon = ({
  item,
  size = "md",
  variant = "solid",
}: {
  item: any;
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "transparent";
}) => {
  const color = getWorkspaceColor(item);
  const type = getWorkspaceType(item);
  const IconComponent = WORKSPACE_TYPE_ICONS[type] || CurrencyDollarIcon;
  const s = size === "sm" ? 16 : size === "lg" ? 32 : 20;
  const iconS = size === "sm" ? 10 : size === "lg" ? 24 : 12;

  if (variant === "transparent") {
    return (
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{ width: s, height: s }}
      >
        <span style={{ width: iconS, height: iconS, display: "flex" }}>
          <IconComponent style={{ color }} className="w-full h-full" />
        </span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center justify-center rounded-md shrink-0"
      style={{ width: s, height: s, backgroundColor: color }}
    >
      <span style={{ width: iconS, height: iconS, display: "flex" }}>
        <IconComponent className="text-white w-full h-full" />
      </span>
    </span>
  );
};

const PipelinePicker = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appContext = useContext<any>(AppContext);
  const navigate = useNavigate();

  const { invalidMaxPipelines } = useUpgradePlan();

  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const projects = useSelector((state: any) => state.projectReducer.get("projects"));
  const teamPipelines =
    useSelector((state: any) => state.tenant.get("teamPipelines")) || fromJS([]);

  const handAddPipeline = () => {
    const totalPipelines = teamPipelines.size;
    if (invalidMaxPipelines(totalPipelines)) return;
    navigate("/workspaces/new");
  };

  const renderList = () => {
    return projects.map((item) => {
      const workspaceName = item.get("name") || "Blank pipeline";
      const isSelected = item.get("id") == project.get("id");
      return (
        <div
          key={item.get("id")}
          className={
            "w-full flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-100 rounded-md"
          }
        >
          <MenuItem>
            <div
              className={
                "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer flex items-center w-full"
              }
              onClick={() => {
                // Reset AI Content Redux data when switching workspace
                dispatch(AIContentAction.resetAIContentState() as any);
                handleOnClickProject(item.toJS(), appContext, navigate);
                // Save last selected workspace for "remember last workspace"
                updateCurrentUserFilters({ currentWorkspaceId: item.get("id") });
              }}
            >
              <PipelineIcon item={item} size="sm" />
              <div className={"w-full text-xs md:text-sm truncate"}>{workspaceName}</div>
            </div>
          </MenuItem>
          <div>{isSelected && <CheckCircleIcon className={"w-3 md:w-4 text-blue-900"} />}</div>
        </div>
      );
    });
  };

  return (
    <Menu as="div">
      <MenuButton className="inline-flex items-center space-x-2 text-sm text-blue-900 w-full outline-none">
        <div className={"w-full h-12 flex items-center space-x-1.5 md:space-x-2"}>
          <PipelineIcon item={project} />
          <div className={"text-xs md:text-sm truncate max-w-20 md:max-w-32"}>
            {project.get("name") || t("No pipeline")}
          </div>
          <div className="shrink-0">
            <ChevronDownIcon className={"w-3 md:w-4"} />
          </div>
        </div>
      </MenuButton>
      <MenuItems className="rounded-sm absolute left-0 z-20 mt-2 origin-top-left bg-white ring-opacity-5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-leave:duration-75 data-enter:ease-out data-leave:ease-in">
        <div
          className={
            "min-w-48 md:min-w-56 p-2 bg-white rounded-lg shadow-lg border border-gray-200 space-y-2"
          }
        >
          {/* Pipeline list with scroll hint */}
          <div className="relative">
            <div className={"overflow-y-auto max-h-48 space-y-0.5 pr-0.5"}>{renderList()}</div>
            {/* Fade gradient — hints that content is scrollable */}
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 h-6"
              style={{
                background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))",
              }}
            />
          </div>
          {/* Add pipeline — always visible, outside scroll area */}
          <div
            onClick={handAddPipeline}
            className="mt-1 w-full h-7 px-1.5 md:px-2 cursor-pointer hover:bg-gray-100 items-center flex rounded-md gap-1.5 text-gray-500 hover:text-gray-800 transition-colors"
          >
            <PlusIcon className="w-3 md:w-4 shrink-0" />
            <span className="text-xs md:text-sm">{t("Add pipeline")}</span>
          </div>
          <div className={"w-full pt-2 border-t border-slate-100"}>
            <div
              onClick={() => {
                navigate(
                  `/workspaces/${project.get("id")}/settings/user?item=profile&key=User%20Settings`
                );
              }}
              className={
                "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer items-center flex rounded-md hover:bg-gray-100 w-full"
              }
            >
              <UserIcon className={"w-3 md:w-4"} />
              <div className="text-xs md:text-sm">{t("Account settings")}</div>
            </div>
            <div
              onClick={() => {
                navigate(
                  `/workspaces/${project.get("id")}/settings/pipeline?item=general&key=Pipeline%20Settings`
                );
              }}
              className={
                "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer items-center flex rounded-md hover:bg-gray-100 w-full"
              }
            >
              <Cog6ToothIcon className={"w-3 md:w-4"} />
              <div className="text-xs md:text-sm">{t("Pipeline settings")}</div>
            </div>
          </div>
          <div className={"w-full pt-2 border-t border-slate-100"}>
            <div
              onClick={() => {
                navigate(
                  `/workspaces/${project.get("id")}/settings/team?item=member&key=Team%20Settings`
                );
              }}
              className={
                "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer items-center flex rounded-md hover:bg-gray-100 w-full"
              }
            >
              <UserPlusIcon className={"w-3 md:w-4"} />
              <div className="text-xs md:text-sm">{t("Invite members")}</div>
            </div>
          </div>
          <div className={"w-full pt-2 border-t border-slate-100"}>
            <div
              onClick={() => {
                navigate(`/workspaces/${project.get("id")}/settings/apps?key=Apps`);
              }}
              className={
                "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer items-center flex rounded-md hover:bg-gray-100 w-full"
              }
            >
              <PackagePlusIcon className={"w-3 md:w-4"} />
              <div className="text-xs md:text-sm">{t("Apps and integrations")}</div>
            </div>
          </div>
          <div className={"w-full pt-2 border-t border-slate-100"}>
            <div
              onClick={() => {
                logout();
                window.location.href = "/";
              }}
              className={
                "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer items-center flex rounded-md hover:bg-gray-100 w-full"
              }
            >
              <ArrowRightStartOnRectangleIcon className={"w-3 md:w-4"} />
              <div className="text-xs md:text-sm">{t("Sign out")}</div>
            </div>
          </div>
        </div>
      </MenuItems>
    </Menu>
  );
};
export default PipelinePicker;
